import { c as _c } from "react/compiler-runtime";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";

// Badge text matches label-sm spec (12px / 16px line-height / 550 weight).
// Total height: py-0.5 (2px top + 2px bottom) + 16px line = 20px.
const badgeVariants = cva("inline-flex shrink-0 items-center gap-1 py-0.5 px-2 font-sans text-xs leading-4 font-[550] whitespace-nowrap transition-[box-shadow] focus-visible:outline-none", {
  variants: {
    variant: {
      primary: ["bg-foreground text-background", "focus-visible:ring-[3px] focus-visible:ring-ring/50"],
      secondary: ["bg-secondary text-foreground", "focus-visible:ring-[3px] focus-visible:ring-ring/50"],
      outline: ["bg-transparent border border-border text-foreground", "focus-visible:ring-[3px] focus-visible:ring-ring/50"],
      ghost: ["bg-transparent text-foreground", "focus-visible:ring-[3px] focus-visible:ring-ring/50"],
      destructive: ["bg-destructive text-primary-foreground", "focus-visible:ring-[3px] focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"]
    },
    roundness: {
      default: "rounded-sm",
      round: "rounded-full"
    }
  },
  defaultVariants: {
    variant: "primary",
    roundness: "default"
  }
});
interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}
function Badge(t0) {
  const $ = _c(22);
  let children;
  let className;
  let leftIcon;
  let props;
  let rightIcon;
  let roundness;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      roundness,
      leftIcon,
      rightIcon,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = leftIcon;
    $[4] = props;
    $[5] = rightIcon;
    $[6] = roundness;
    $[7] = variant;
  } else {
    children = $[1];
    className = $[2];
    leftIcon = $[3];
    props = $[4];
    rightIcon = $[5];
    roundness = $[6];
    variant = $[7];
  }
  let t1;
  if ($[8] !== className || $[9] !== roundness || $[10] !== variant) {
    t1 = cn(badgeVariants({
      variant,
      roundness
    }), className);
    $[8] = className;
    $[9] = roundness;
    $[10] = variant;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== leftIcon) {
    t2 = leftIcon != null && <span className="size-3 shrink-0 flex items-center justify-center [&>svg]:size-full">{leftIcon}</span>;
    $[12] = leftIcon;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== rightIcon) {
    t3 = rightIcon != null && <span className="size-3 shrink-0 flex items-center justify-center [&>svg]:size-full">{rightIcon}</span>;
    $[14] = rightIcon;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== children || $[17] !== props || $[18] !== t1 || $[19] !== t2 || $[20] !== t3) {
    t4 = <span data-slot="badge" className={t1} {...props}>{t2}{children}{t3}</span>;
    $[16] = children;
    $[17] = props;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
}
export { Badge, badgeVariants, type BadgeProps };